import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import {
  buildTestCounts,
  type TestStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { queryTestExecutions, queryTestExecutionStatusSummary } from '@/features/executions/data';
import { CLONE_TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/cloneTable/cloneTableConstants';
import { EntityTrendsPageShell } from '@/features/shared/components/EntityTrendsPageShell';
import { useCloneDateWindow } from '@/features/shared/hooks/useCloneDateWindow';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { resolveEntityTags } from '@/features/shared/tags';
import { TestExecutionsTableClone } from '@/features/tests/components/TestExecutionsTableClone';
import { TestsTableClone } from '@/features/tests/components/TestsTableClone';
import { queryTests, queryTestStatusSummary } from '@/features/tests/data/tests';
import { TestExecutionsCloneToolbar } from '@/features/tests/pages/TestExecutionsCloneToolbar';
import {
  cloneExecutionDefaultColumnVisibilityModel,
  cloneExecutionDefaultSortDirection,
  cloneExecutionDefaultSortKey,
  cloneExecutionSortFieldMap,
  type CloneExecutionSortDirection,
  type CloneExecutionSortKey,
} from '@/features/tests/pages/testExecutionsPageCloneConfig';
import { CloneToolbar, type CloneDateQuickFilter } from '@/features/tests/pages/TestsCloneToolbar';
import {
  cloneDefaultColumnVisibilityModel,
  cloneDefaultSortDirection,
  cloneDefaultSortKey,
  cloneSortFieldMap,
  type CloneSortDirection,
  type CloneSortKey,
} from '@/features/tests/pages/testsPageCloneConfig';
import { testExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { gridPaths, queryKeys, useResolvedFeatureFlags } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import { Box } from '@/ui/primitives';
import { Tab, Tabs, type GridColumnVisibilityModel } from '@/ui/primitives';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  TestExecutionDto,
  TestExecutionStatus,
  TestExecutionStatusSummaryDto,
  TestListItemDto,
  TestSummaryDto,
} from '@flowqa/shared';

type CloneTestsPanelTab = 'tests' | 'executions';

export const TestsPage = (): ReactElement => {
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const cloneTableEnabled = featureFlags?.testsCloneTable ?? false;
  const viewModel = useEntityTrendsPage<
    TestListItemDto,
    TestSummaryDto | TestExecutionStatusSummaryDto
  >({
    loadingMessage: 'Loading tests...',
    metricsScope: 'test',
    gridPath: gridPaths.tests,
    executionGridPath: gridPaths.testExecutions,
    entitySearchField: 'name',
    executionSearchField: 'testName',
    statusOptions: testExecutionSummaryStatusOptions.map((option) => ({
      value: option.value as Exclude<TestStatusKey, 'untested'>,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testExecutionSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.testSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.testExecutionSummaryPrefix,
    queryLatestSummary: queryTestStatusSummary,
    queryExecutionSummary: queryTestExecutionStatusSummary,
    buildCounts: buildTestCounts,
    selectThresholds: (thresholds) => thresholds.tests,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
      },
    ): Promise<QueryResult<TestListItemDto>> =>
      queryTests(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          suites: { ids: true, items: true, primary: true },
          latestExecution: true,
          metrics: true,
          metricDeltas: deltasEnabled && previousRangeStartIso !== null,
        },
      }),
  });

  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneSearch, setDebouncedCloneSearch] = useState('');
  const [cloneColumnVisibilityModel, setCloneColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(cloneDefaultColumnVisibilityModel);
  const [cloneSortKey, setCloneSortKey] = useState<CloneSortKey>(cloneDefaultSortKey);
  const [cloneSortDirection, setCloneSortDirection] =
    useState<CloneSortDirection>(cloneDefaultSortDirection);
  const [cloneFilterStatuses, setCloneFilterStatuses] = useState<TestExecutionStatus[]>([]);
  const [cloneFilterSuites, setCloneFilterSuites] = useState<string[]>([]);
  const [cloneFilterTags, setCloneFilterTags] = useState<string[]>([]);
  const [cloneVisibleRows, setCloneVisibleRows] = useState<TestListItemDto[]>([]);
  const [cloneActiveTab, setCloneActiveTab] = useState<CloneTestsPanelTab>('tests');
  const [debouncedCloneExecutionSearch, setDebouncedCloneExecutionSearch] = useState('');
  const [cloneExecutionColumnVisibilityModel, setCloneExecutionColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(cloneExecutionDefaultColumnVisibilityModel);
  const [cloneExecutionSortKey, setCloneExecutionSortKey] = useState<CloneExecutionSortKey>(
    cloneExecutionDefaultSortKey,
  );
  const [cloneExecutionSortDirection, setCloneExecutionSortDirection] =
    useState<CloneExecutionSortDirection>(cloneExecutionDefaultSortDirection);
  const [cloneExecutionFilterStatuses, setCloneExecutionFilterStatuses] = useState<
    TestExecutionStatus[]
  >([]);
  const [cloneExecutionFilterSuites, setCloneExecutionFilterSuites] = useState<string[]>([]);
  const [cloneExecutionFilterTags, setCloneExecutionFilterTags] = useState<string[]>([]);
  const [cloneVisibleExecutionRows, setCloneVisibleExecutionRows] = useState<TestExecutionDto[]>(
    [],
  );

  const cloneAvailableTags = useMemo(() => {
    const tags = new Set<string>();
    cloneVisibleRows.forEach((row) => {
      resolveEntityTags(row).forEach((tag) => {
        tags.add(tag);
      });
    });
    return Array.from(tags.values()).sort((left, right) => left.localeCompare(right));
  }, [cloneVisibleRows]);
  const cloneAvailableSuites = useMemo(() => {
    const options = new Map<string, string>();
    cloneVisibleRows.forEach((row) => {
      const suiteItems = row.suites?.items ?? [];
      suiteItems.forEach((suite) => {
        if (!options.has(suite.id)) {
          options.set(suite.id, suite.name);
        }
      });
      const suiteIds = row.suites?.ids ?? [];
      suiteIds.forEach((suiteId) => {
        if (!options.has(suiteId)) {
          options.set(suiteId, suiteId);
        }
      });
    });
    return Array.from(options.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [cloneVisibleRows]);
  const cloneExecutionAvailableTags = useMemo(() => {
    const tags = new Set<string>();
    cloneVisibleExecutionRows.forEach((row) => {
      resolveEntityTags(row).forEach((tag) => {
        tags.add(tag);
      });
    });
    return Array.from(tags.values()).sort((left, right) => left.localeCompare(right));
  }, [cloneVisibleExecutionRows]);
  const cloneExecutionAvailableSuites = useMemo(() => {
    const options = new Map<string, string>();
    cloneVisibleExecutionRows.forEach((row) => {
      const suiteId = row.suiteId ?? null;
      const suiteLabel = row.suiteName ?? row.suiteSlug ?? suiteId;
      if (suiteId && suiteLabel && !options.has(suiteId)) {
        options.set(suiteId, suiteLabel);
      }
    });
    return Array.from(options.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [cloneVisibleExecutionRows]);
  const handleDebouncedCloneSearchChange = useCallback((value: string) => {
    setDebouncedCloneSearch(value);
  }, []);
  const handleDebouncedCloneExecutionSearchChange = useCallback((value: string) => {
    setDebouncedCloneExecutionSearch(value);
  }, []);
  const showCloneDeltas = viewModel.deltasEnabled;
  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> => {
      const mappedFilters = input.filters.map((filter) =>
        filter.field === 'statusFilter'
          ? {
              ...filter,
              field: 'latestExecutionStatus',
            }
          : filter,
      );
      const toolbarFilters = mergeFilters(
        buildInFilter('latestExecutionStatus', cloneFilterStatuses),
        buildInFilter('suiteId', cloneFilterSuites),
        buildInFilter('tags', cloneFilterTags),
      );
      const sortOverride: ApiSort = {
        field: cloneSortFieldMap[cloneSortKey],
        direction: cloneSortDirection,
      };
      return queryTests(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          sorts: [sortOverride],
          filters: [...mappedFilters, ...toolbarFilters],
          metricsStartDay: cloneDateWindow.startDay ?? undefined,
          metricsEndDay: cloneDateWindow.endDay ?? undefined,
          previousMetricsStartDay: cloneDateWindow.previousStartDay ?? undefined,
          previousMetricsEndDay: cloneDateWindow.previousEndDay ?? undefined,
          include: {
            suites: { ids: true, items: true, primary: true },
            latestExecution: true,
            metrics: true,
            metricDeltas: showCloneDeltas,
          },
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneFilterStatuses,
      cloneFilterSuites,
      cloneFilterTags,
      cloneSortDirection,
      cloneSortKey,
      showCloneDeltas,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestExecutionDto>> => {
      const isLatestQuickFilter =
        cloneDateQuickFilter === 'latest' && cloneFromDate === null && cloneToDate === null;
      const baseFilters = isLatestQuickFilter
        ? input.filters.filter(
            (filter) =>
              filter.field !== 'timestamp' &&
              filter.field !== 'createdAt' &&
              filter.field !== 'isLatest',
          )
        : input.filters;
      const toolbarFilters = mergeFilters(
        buildInFilter('status', cloneExecutionFilterStatuses),
        buildInFilter('suiteId', cloneExecutionFilterSuites),
        buildInFilter('tags', cloneExecutionFilterTags),
        isLatestQuickFilter
          ? buildEqFilterValue('isLatest', true)
          : buildDateRangeFilters(
              'timestamp',
              cloneDateWindow.startDay
                ? dayjs(cloneDateWindow.startDay).startOf('day').toISOString()
                : null,
              cloneDateWindow.endDay
                ? dayjs(cloneDateWindow.endDay).endOf('day').toISOString()
                : null,
            ),
      );
      const sortOverride: ApiSort = {
        field: cloneExecutionSortFieldMap[cloneExecutionSortKey],
        direction: cloneExecutionSortDirection,
      };
      return queryTestExecutions(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          search: debouncedCloneExecutionSearch,
          sorts: [sortOverride],
          filters: [...baseFilters, ...toolbarFilters],
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.startDay,
      cloneDateQuickFilter,
      cloneFromDate,
      cloneToDate,
      cloneExecutionFilterStatuses,
      cloneExecutionFilterSuites,
      cloneExecutionFilterTags,
      cloneExecutionSortDirection,
      cloneExecutionSortKey,
      debouncedCloneExecutionSearch,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No tests found for selected date range'
    : formatRangeWindowNoDataText('No tests found', cloneDateQuickFilter, false);
  const cloneExecutionsNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No test executions found for selected date range'
    : formatRangeWindowNoDataText('No test executions found', cloneDateQuickFilter, false);
  const cloneStatusFilterKey = useMemo(
    () => [...cloneFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterStatuses],
  );
  const cloneSuiteFilterKey = useMemo(
    () => [...cloneFilterSuites].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterSuites],
  );
  const cloneTagFilterKey = useMemo(
    () => [...cloneFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTags],
  );
  const cloneExecutionStatusFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterStatuses],
  );
  const cloneExecutionSuiteFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterSuites].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterSuites],
  );
  const cloneExecutionTagFilterKey = useMemo(
    () => [...cloneExecutionFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterTags],
  );
  const cloneContentOverride = cloneTableEnabled ? (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="md"
            sx={{
              minWidth: 0,
              minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs
                value={cloneActiveTab}
                onChange={(_event, next) => {
                  const nextTab = next as CloneTestsPanelTab;
                  setCloneActiveTab(nextTab);
                  if (nextTab === 'tests' && cloneDateQuickFilter === 'latest') {
                    setCloneDateQuickFilter('7d');
                  }
                }}
                aria-label="Clone tests panel tabs"
              >
                <Tab label="Tests" value="tests" />
                <Tab label="Executions" value="executions" />
              </Tabs>
            </Box>
            <Box sx={{ flex: '0 0 auto', display: cloneActiveTab === 'tests' ? 'block' : 'none' }}>
              <CloneToolbar
                title={undefined}
                onDebouncedCloneSearchChange={handleDebouncedCloneSearchChange}
                columnVisibilityModel={cloneColumnVisibilityModel}
                onColumnVisibilityModelChange={setCloneColumnVisibilityModel}
                sortKey={cloneSortKey}
                sortDirection={cloneSortDirection}
                onSortChange={(nextSortKey, nextSortDirection) => {
                  setCloneSortKey(nextSortKey);
                  setCloneSortDirection(nextSortDirection);
                }}
                filterStatuses={cloneFilterStatuses}
                onFilterStatusesChange={setCloneFilterStatuses}
                filterSuites={cloneFilterSuites}
                onFilterSuitesChange={setCloneFilterSuites}
                suiteOptions={cloneAvailableSuites}
                filterTags={cloneFilterTags}
                onFilterTagsChange={setCloneFilterTags}
                dateQuickFilter={cloneDateQuickFilter}
                onDateQuickFilterChange={(next) => {
                  setCloneDateQuickFilter(next);
                  setCloneFromDate(null);
                  setCloneToDate(null);
                }}
                fromDate={cloneFromDate}
                toDate={cloneToDate}
                onFromDateChange={setCloneFromDate}
                onToDateChange={setCloneToDate}
                availableTags={cloneAvailableTags}
              />
            </Box>
            <Box
              sx={{
                flex: '0 0 auto',
                display: cloneActiveTab === 'executions' ? 'block' : 'none',
              }}
            >
              <TestExecutionsCloneToolbar
                title={undefined}
                onDebouncedCloneSearchChange={handleDebouncedCloneExecutionSearchChange}
                columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                onColumnVisibilityModelChange={setCloneExecutionColumnVisibilityModel}
                sortKey={cloneExecutionSortKey}
                sortDirection={cloneExecutionSortDirection}
                onSortChange={(nextSortKey, nextSortDirection) => {
                  setCloneExecutionSortKey(nextSortKey);
                  setCloneExecutionSortDirection(nextSortDirection);
                }}
                filterStatuses={cloneExecutionFilterStatuses}
                onFilterStatusesChange={setCloneExecutionFilterStatuses}
                filterSuites={cloneExecutionFilterSuites}
                onFilterSuitesChange={setCloneExecutionFilterSuites}
                suiteOptions={cloneExecutionAvailableSuites}
                filterTags={cloneExecutionFilterTags}
                onFilterTagsChange={setCloneExecutionFilterTags}
                dateQuickFilter={cloneDateQuickFilter}
                onDateQuickFilterChange={(next) => {
                  setCloneDateQuickFilter(next);
                  setCloneFromDate(null);
                  setCloneToDate(null);
                }}
                fromDate={cloneFromDate}
                toDate={cloneToDate}
                onFromDateChange={setCloneFromDate}
                onToDateChange={setCloneToDate}
                availableTags={cloneExecutionAvailableTags}
              />
            </Box>
          </Stack>
        </Box>
      }
      sx={{ backgroundColor: 'background.paper' }}
    >
      <Box sx={{ display: cloneActiveTab === 'tests' ? 'block' : 'none' }}>
        <TestsTableClone
          persistKey={null}
          queryKey={[
            'tests-table-clone',
            cloneDateQuickFilter,
            viewModel.selectedOrganisationId ?? null,
            viewModel.selectedProjectId ?? null,
            showCloneDeltas ? 'deltas-on' : 'deltas-off',
            cloneSortKey,
            cloneSortDirection,
            cloneStatusFilterKey,
            cloneSuiteFilterKey,
            cloneTagFilterKey,
            cloneDateWindow.startDay,
            cloneDateWindow.endDay,
          ]}
          queryFn={cloneQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={10}
          showDeltas={showCloneDeltas}
          sparklineRangeDays={cloneDateWindow.sparklineRangeDays}
          metricsStartDay={cloneDateWindow.startDay ?? undefined}
          metricsEndDay={cloneDateWindow.endDay ?? undefined}
          noRowsText={cloneNoRowsText}
          showToolbar={false}
          disableColumnSorting
          disableColumnMenu
          search={debouncedCloneSearch}
          onRowsChange={(rows) => {
            setCloneVisibleRows(rows);
          }}
          columnVisibilityModel={cloneColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setCloneColumnVisibilityModel({
              ...nextModel,
              name: true,
            });
          }}
        />
      </Box>
      <Box sx={{ display: cloneActiveTab === 'executions' ? 'block' : 'none' }}>
        <TestExecutionsTableClone
          persistKey={null}
          queryKey={[
            'test-executions-table-clone',
            cloneDateQuickFilter,
            viewModel.selectedOrganisationId ?? null,
            viewModel.selectedProjectId ?? null,
            cloneExecutionSortKey,
            cloneExecutionSortDirection,
            cloneExecutionStatusFilterKey,
            cloneExecutionSuiteFilterKey,
            cloneExecutionTagFilterKey,
            debouncedCloneExecutionSearch,
            cloneDateWindow.startDay,
            cloneDateWindow.endDay,
          ]}
          queryFn={cloneExecutionQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={10}
          noRowsText={cloneExecutionsNoRowsText}
          showToolbar={false}
          disableColumnSorting
          disableColumnMenu
          search={debouncedCloneExecutionSearch}
          onRowsChange={(rows) => {
            setCloneVisibleExecutionRows(rows);
          }}
          columnVisibilityModel={cloneExecutionColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setCloneExecutionColumnVisibilityModel({
              ...nextModel,
              name: true,
            });
          }}
        />
      </Box>
    </Panel>
  ) : undefined;

  return (
    <Stack gap="md">
      <EntityTrendsPageShell
        viewModel={viewModel}
        title="Tests"
        addButtonLabel="Add Test"
        searchAriaLabel="Search tests"
        chartTabsAriaLabel="Tests chart tabs"
        pageTabsAriaLabel="Tests page tabs"
        entityTabLabel="Tests"
        chartEmptyStateTitle="No test executions found"
        entityTableNoRowsText="No tests found"
        executionTableNoRowsText="No test executions found"
        persistKey="tests-table"
        executionPersistKey="test-executions-table"
        contentOverride={cloneContentOverride}
        renderEntityTable={({
          queryKey,
          queryFn,
          fixedFilters,
          showDeltas,
          persistKey,
          noRowsText,
        }) => (
          <TestsTableClone
            persistKey={persistKey}
            queryKey={queryKey}
            queryFn={queryFn}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            showDeltas={showDeltas}
            fixedFilters={fixedFilters}
            noRowsText={noRowsText}
          />
        )}
        renderExecutionsTable={({ fixedFilters, persistKey, noRowsText }) => (
          <TestExecutionsTableClone
            persistKey={persistKey}
            fixedFilters={fixedFilters}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            noRowsText={noRowsText}
          />
        )}
      />
    </Stack>
  );
};
